import app from 'flarum/forum/app';
import { isDiscussionPage, getDiscussionModel, getDiscussionId, getDiscussionSlug, getCurrentPage, getTotalPages, getNearFromUrl, navigateToDiscussionPage, currentUrlKey } from './paginationUtils';
let renderTimer = null;
let observer = null;
let urlWatchTimer = null;
let keepAliveTimer = null;
let lastRenderKey = '';
let lastUrl = '';
function isMobile() {
  return window.matchMedia('(max-width: 800px)').matches;
}
function classicPaginationEnabled() {
  const v = app.forum.attribute('magicread_enable_pagination');
  return v !== false && !app.forum.attribute('magicread_enable_discussion_pager') && !isMobile();
}
function getNavRoot(root) {
  var _root$querySelector, _root$querySelector2;
  return root.querySelector('.DiscussionPage-nav') || ((_root$querySelector = root.querySelector('.PostStreamScrubber')) == null ? void 0 : _root$querySelector.closest('.DiscussionPage-nav')) || ((_root$querySelector2 = root.querySelector('.item-scrubber')) == null ? void 0 : _root$querySelector2.closest('.DiscussionPage-nav'));
}
function getScrubberElement(navRoot) {
  return navRoot.querySelector('.item-scrubber') || navRoot.querySelector('.PostStreamScrubber');
}
function findTimelineHost() {
  const root = document.querySelector('.DiscussionPage');
  if (!root) return null;
  const navRoot = getNavRoot(root);
  if (!navRoot) return null;
  const scrubber = getScrubberElement(navRoot);
  let host = navRoot.querySelector('.MagicRead-TimelinePagerHost');
  if (!host) {
    host = document.createElement('div');
    host.className = 'MagicRead-TimelinePagerHost';
  }
  if (scrubber && scrubber.parentNode) {
    if (host.parentNode !== scrubber.parentNode || host.previousElementSibling !== scrubber) {
      host.remove();
      if (scrubber.nextSibling) {
        scrubber.parentNode.insertBefore(host, scrubber.nextSibling);
      } else {
        scrubber.parentNode.appendChild(host);
      }
    }
  } else if (host.parentNode !== navRoot) {
    host.remove();
    navRoot.appendChild(host);
  }
  return host;
}
function removeClassicPagerHost() {
  document.querySelectorAll('.MagicRead-TimelinePagerHost').forEach(el => el.remove());
  lastRenderKey = '';
}
function buildClassicPager(discussion, currentPage, totalPages) {
  const wrap = document.createElement('div');
  wrap.className = 'MagicRead-TimelinePager';
  const input = document.createElement('input');
  input.type = 'number';
  input.min = '1';
  input.max = String(totalPages);
  input.step = '1';
  input.value = String(currentPage);
  input.inputMode = 'numeric';
  input.setAttribute('aria-label', String(app.translator.trans('forumaker-magicread.forum.pager.input_label')));
  const fitInputWidth = () => {
    const len = Math.max(1, input.value.length, String(totalPages).length);
    input.style.width = "".concat(Math.min(Math.max(len + 0.2, 2), 4), "ch");
  };
  input.addEventListener('input', fitInputWidth);
  fitInputWidth();
  const sep = document.createElement('span');
  sep.className = 'MagicRead-Sep';
  sep.textContent = '/';
  const total = document.createElement('span');
  total.className = 'MagicRead-Total';
  total.textContent = String(totalPages);
  const submit = () => {
    const wanted = parseInt(input.value, 10);
    if (!Number.isFinite(wanted)) return;
    const page = Math.min(Math.max(wanted, 1), totalPages);
    navigateToDiscussionPage(discussion, page);
  };
  input.addEventListener('keydown', e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  });
  input.addEventListener('change', submit);
  wrap.appendChild(input);
  wrap.appendChild(sep);
  wrap.appendChild(total);
  return wrap;
}
function buildRenderKey(discussion, currentPage, totalPages) {
  return [getDiscussionId(discussion), getDiscussionSlug(discussion), currentPage, totalPages, getNearFromUrl(), location.pathname, isMobile() ? 'm' : 'd'].join('|');
}
function applyClassicPagination(force) {
  if (force === void 0) {
    force = false;
  }
  const enabled = classicPaginationEnabled();
  const onDiscussion = isDiscussionPage();
  document.body.classList.toggle('MagicRead--classicPaginationEnabled', enabled && onDiscussion);
  if (!enabled || !onDiscussion) {
    removeClassicPagerHost();
    return;
  }
  const discussion = getDiscussionModel();
  if (!discussion) return;
  const totalPages = getTotalPages(discussion);
  const currentPage = getCurrentPage(totalPages);
  const renderKey = buildRenderKey(discussion, currentPage, totalPages);
  const host = findTimelineHost();
  if (!host) return;
  if (!force && renderKey === lastRenderKey && host.childElementCount > 0) {
    return;
  }
  lastRenderKey = renderKey;
  host.replaceChildren(buildClassicPager(discussion, currentPage, totalPages));
}
function rerenderAfterRouteChange() {
  scheduleClassicPaginationRender(0, true);
  setTimeout(() => scheduleClassicPaginationRender(80, true), 80);
  setTimeout(() => scheduleClassicPaginationRender(180, true), 180);
  setTimeout(() => scheduleClassicPaginationRender(320, true), 320);
  setTimeout(() => scheduleClassicPaginationRender(500, true), 500);
}
function startUrlWatch() {
  if (urlWatchTimer) return;
  lastUrl = currentUrlKey();
  urlWatchTimer = window.setInterval(() => {
    const now = currentUrlKey();
    if (now !== lastUrl) {
      lastUrl = now;
      rerenderAfterRouteChange();
    }
  }, 120);
}
function startKeepAlive() {
  if (keepAliveTimer) return;
  keepAliveTimer = window.setInterval(() => {
    if (!classicPaginationEnabled() || !isDiscussionPage()) return;
    const host = findTimelineHost();
    if (!host || !host.firstElementChild) {
      scheduleClassicPaginationRender(0, true);
    }
  }, 400);
}
export function scheduleClassicPaginationRender(delay, force) {
  if (delay === void 0) {
    delay = 0;
  }
  if (force === void 0) {
    force = false;
  }
  if (renderTimer) window.clearTimeout(renderTimer);
  renderTimer = window.setTimeout(() => {
    applyClassicPagination(force);
  }, delay);
}
export function mountClassicPagination() {
  var _ref;
  if (!urlWatchTimer) startUrlWatch();
  if (!keepAliveTimer) startKeepAlive();
  if (observer) return;
  observer = new MutationObserver(mutations => {
    if (!classicPaginationEnabled() || !isDiscussionPage()) return;
    const shouldReact = mutations.some(mutation => Array.from(mutation.addedNodes).some(node => {
      var _node$closest, _node$matches, _node$querySelector;
      if (!(node instanceof HTMLElement)) return false;
      if ((_node$closest = node.closest) != null && _node$closest.call(node, '.MagicRead-TimelinePagerHost')) return false;
      return ((_node$matches = node.matches) == null ? void 0 : _node$matches.call(node, '.DiscussionPage, .DiscussionPage-nav, .PostStreamScrubber, .item-scrubber, .PostStream, .PostStream-item')) || !!((_node$querySelector = node.querySelector) != null && _node$querySelector.call(node, '.DiscussionPage, .DiscussionPage-nav, .PostStreamScrubber, .item-scrubber, .PostStream, .PostStream-item'));
    }));
    if (!shouldReact) return;
    scheduleClassicPaginationRender(30, true);
  });
  const root = (_ref = document.querySelector('.DiscussionPage')) != null ? _ref : document.body;
  observer.observe(root, {
    childList: true,
    subtree: true
  });
}
export function unmountClassicPagination() {
  if (renderTimer) {
    window.clearTimeout(renderTimer);
    renderTimer = null;
  }
  if (urlWatchTimer) {
    window.clearInterval(urlWatchTimer);
    urlWatchTimer = null;
  }
  if (keepAliveTimer) {
    window.clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  removeClassicPagerHost();
}
flarum.reg.add('forumaker-magicread', 'forum/components/classicPagination', { scheduleClassicPaginationRender: scheduleClassicPaginationRender,mountClassicPagination: mountClassicPagination,unmountClassicPagination: unmountClassicPagination, });